import { useState, useMemo, useCallback, useEffect } from "react";
import { useDebounce } from "../../../../../hooks/useDebounce";
import LimitSelect from "../../../../../components/elements/LimitSelect";
import Search from "../../../../../components/form/Search";
import Table from "../../../../../components/elements/table/Table";
import dayjs from "dayjs";
import { lngDetect } from "../../../../../helpers/lngDetect";
import { handleFilterChange } from "../../../../../helpers";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useOutletContext, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import TicketSaleDrawer from "./TicketSaleDrawer";
import { organizationTeamItem } from "../../../../../services/organizationService";
import Skeleton from "../../../../loader/Skeleton";
import ExportDropdown from "../ExportDropdown";
import Button from "../../../../form/Button";
import {
  reportCountList,
  reportExport,
} from "../../../../../services/reportService";
import { useOrganization } from "../../../../../context/OrganizationContext";
import {
  createCSVContent,
  createExcelFile,
  downloadFile,
} from "../CommonExport";
import { t } from "i18next";

// Constants moved outside component to prevent recreation
const TICKET_TYPE_STYLES = {
  ticket: {
    backgroundColor: "#EDE9FE",
    color: "#7C3AED",
    borderColor: "#8B5CF6",
  },
  "add-on": {
    backgroundColor: "#ECFDF5",
    color: "#059669",
    borderColor: "#10B981",
  },
};

const DEFAULT_STYLE = {
  backgroundColor: "#F3F4F6",
  color: "#374151",
  borderColor: "#6B7280",
};

const getTicketTypeStyle = (type) => {
  return TICKET_TYPE_STYLES[type?.toLowerCase()] || DEFAULT_STYLE;
};

const TicketSaleList = () => {
  const { event } = useOutletContext();
  const { selectedOrganization } = useOrganization();
  const [selectedItems, setSelectedItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState({ isOpen: false, data: null });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [searchParams] = useSearchParams();
  const ticketType = searchParams.get("ticketType");
  const [eventCountFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  // Initialize applied filters based on URL param or default
  const [appliedFilters, setAppliedFilters] = useState(() => {
    if (ticketType === "ticket") {
      return { ticketType: ["ticket"] };
    } else if (ticketType) {
      return { ticketType: ["addons-event"] };
    } else {
      return { ticketType: ["addons-event", "ticket"] };
    }
  });

  const { data: eventCount, isLoading: eventCountLoading } = useQuery({
    queryKey: [
      "eventCountData",
      eventCountFilters,
      event?.data?._id,
      appliedFilters.startDate,
      appliedFilters.endDate,
    ],
    queryFn: async () => {
      const res = await reportCountList({
        ...eventCountFilters,
        ...appliedFilters,
        event: [event?.data?._id],
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
    enabled: !!event?.data?._id,
  });

  // State for drawer filters (separate from applied filters)
  const [drawerFilters, setDrawerFilters] = useState(() => {
    if (ticketType === "ticket") {
      return { ticketType: ["ticket"] };
    } else if (ticketType) {
      return { ticketType: ["addons-event"] };
    } else {
      return { ticketType: ["addons-event", "ticket"] };
    }
  });

  // Update applied filters when URL param changes
  useEffect(() => {
    const newFilter = (() => {
      if (ticketType === "ticket") {
        return { ticketType: ["ticket"] };
      } else if (ticketType) {
        return { ticketType: ["addons-event"] };
      } else {
        return { ticketType: ["addons-event", "ticket"] };
      }
    })();

    setAppliedFilters(newFilter);
    setDrawerFilters(newFilter);
  }, [ticketType]);

  // Memoized query key - use appliedFilters instead of currentTicketTypeFilter
  const queryKey = useMemo(
    () => [
      "ticketSale",
      filters,
      search,
      appliedFilters.ticketType,
      appliedFilters.startDate,
      appliedFilters.endDate,
      event?.data?._id,
    ],
    [filters, search, appliedFilters, event?.data?._id]
  );

  // Fetch ticket sales data
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const queryParams = {
        ...filters,
        ...appliedFilters, // Use appliedFilters instead of currentTicketTypeFilter
        ...(search && { keyword: search }),
        event: event?.data?._id,
        orderStatus: ["confirmed"],
      };

      const res = await organizationTeamItem(queryParams);
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!appliedFilters?.ticketType?.length,
  });

  // Send file to API
  const sendFileToAPI = useCallback(
    async ({ fileContent, localDownloadStatus, fileName, format, event }) => {
      try {
        const formData = new FormData();
        formData.append("event", event);
        formData.append("file", fileContent, fileName);
        formData.append("type", "sales-by-ticket-type");
        formData.append(
          "name",
          `Ticket Sales Report (${format?.toUpperCase()})`
        );
        formData.append("organization", selectedOrganization);
        formData.append("status", localDownloadStatus ? "success" : "failed");

        const res = await reportExport(formData);
        return res?.status === 200 || res?.status === 201;
      } catch (error) {
        return false;
      }
    },
    [selectedOrganization]
  );

  // Unified export handler
  const handleExport = useCallback(
    async (format) => {
      setIsExportOpen(false);
      toast.info("Preparing export data...");

      try {
        const exportResponse = await organizationTeamItem({
          ...filters,
          ...appliedFilters, // Use appliedFilters for export consistency
          ...(search && { keyword: search }),
          limit: data?.metadata?.totalDocs,
          page: 1,
          event: event?.data?._id,
          orderStatus: ["confirmed"],
        });

        if (exportResponse?.docs?.status !== 200) {
          toast.error(exportResponse?.message || "Failed to fetch export data");
          return;
        }

        const ticketData = exportResponse?.docs?.data || [];
        if (!ticketData || ticketData.length === 0) {
          toast.warning("No data available to export");
          return;
        }

        // Transform data
        const exportData = ticketData.map((sale) => ({
          "Ticket Name": lngDetect(sale?.ticketInfo?.name) || "-",
          "Ticket Type":
            sale?.ticketInfo?.type
              ?.split("-")
              ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              ?.join(" ") || "-",
          "Ticket Quantity": sale?.ticketData?.quantity || 0,
          "Paid Ticket Sole": sale?.totalQuantity || 0,
          "Gross Sale": "$" + (sale?.totalFinalPrice || 0),
        }));

        const filename = `ticket_sales_export_${dayjs().format("YYYY-MM-DD")}`;

        let blob, fullFilename;
        if (format === "csv") {
          const csvContent = createCSVContent(exportData);
          blob = new Blob([csvContent], { type: "text/csv" });
          fullFilename = `${filename}.csv`;
        } else {
          const workbook = createExcelFile(exportData);
          if (format === "xlsx") {
            const excelBuffer = XLSX.write(workbook, {
              bookType: "xlsx",
              type: "array",
            });
            blob = new Blob([excelBuffer], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            fullFilename = `${filename}.xlsx`;
          }
        }

        const downloadSuccess = await downloadFile(blob, fullFilename);

        if (downloadSuccess) {
          toast.success(
            `${format.toUpperCase()} file ${
              window.showSaveFilePicker ? "saved" : "downloaded"
            } successfully!`
          );

          try {
            await sendFileToAPI({
              fileName: fullFilename,
              fileContent: blob,
              localDownloadStatus: downloadSuccess,
              format: format,
              event: event?.data?._id,
            });
          } catch (apiError) {
            console.error("API upload failed:", apiError);
            if (window.showSaveFilePicker) {
              toast.warning("File saved locally but failed to sync to server");
            }
          }
        }
      } catch (error) {
        toast.error("Export failed. Please try again.");
      }
    },
    [
      filters,
      appliedFilters,
      search,
      data?.metadata?.totalDocs,
      event?.data?._id,
      createCSVContent,
      createExcelFile,
      downloadFile,
      sendFileToAPI,
    ]
  );

  // Memoized table head data
  const tableHeadData = [
    {
      _id: "ticketName",
      name: t("Ticket Name"),
      align: "left",
    },
    {
      _id: "ticketType",
      name: t("Ticket Type"),
      align: "left",
    },
    {
      _id: "ticketQuantity",
      name: t("Ticket Quantity"),
      align: "left",
    },
    {
      _id: "paidTicketsSold",
      name: t("Paid Tickets Sold"),
      align: "left",
    },
    {
      _id: "grossSales",
      name: t("Gross Sales"),
      align: "left",
    },
  ];

  // Memoized table data with optimized rendering
  const tableData = useMemo(() => {
    return (
      data?.data?.map((item) => {
        const ticketType = item?.ticketInfo?.type || "general-admission";
        const ticketTypeText = ticketType
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        const ticketTypeStyle = getTicketTypeStyle(ticketType);

        return {
          _id: item?._id,
          rowData: [
            {
              _id: "salesTicket",
              type: "text",
              align: "left",
              cellClass: "!min-w-[120px]",
              data: lngDetect(item?.ticketInfo?.name),
            },
            {
              _id: "salesChannel",
              type: "text",
              align: "left",
              cellClass: "!min-w-[120px]",
              data: (
                <span
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap"
                  style={ticketTypeStyle}
                >
                  {ticketTypeText}
                </span>
              ),
            },
            {
              _id: "ticketCount",
              type: "text",
              align: "left",
              cellClass: "!min-w-[100px] font-medium",
              data: item?.ticketData?.quantity || 0,
            },
            {
              _id: "paidTicketsSold",
              type: "text",
              align: "left",
              cellClass: "!min-w-[120px] text-center font-medium",
              data: item?.totalQuantity || 0,
            },
            {
              _id: "complimentaryTicketsSold",
              type: "text",
              align: "left",
              cellClass: "!min-w-[160px] text-center font-medium",
              data: `${item?.eventData?.currency?.symbol || "$"} ${(
                item?.totalFinalPrice || 0
              ).toFixed(2)}`,
            },
          ],
        };
      }) || []
    );
  }, [data?.data]);

  // Updated handleRunReport to apply drawer filters
  const handleRunReport = useCallback(async () => {
    try {
      // Apply the drawer filters to the actual query
      setAppliedFilters(drawerFilters);

      // Reset to first page when applying new filters
      setFilters((prev) => ({ ...prev, page: 1 }));

      // Close the drawer
      setDrawerOpen({ isOpen: false, data: null });

      // The query will automatically refetch due to appliedFilters change
      toast.success("Filters applied successfully!");
    } catch (error) {
      console.error("Error applying filters:", error);
      toast.error("Failed to apply filters. Please try again.");
    }
  }, [drawerFilters]);

  // Memoized callbacks
  const handleLimitChange = useCallback((val) => {
    handleFilterChange("limit", val, setFilters);
  }, []);

  const handleSearch = useCallback((val) => {
    setKeyword(val);
    handleFilterChange("page", 1, setFilters);
  }, []);

  const handlePageChange = useCallback((val) => {
    handleFilterChange("page", val, setFilters);
    setSelectedItems([]);
  }, []);

  const toggleExportDropdown = useCallback(() => {
    setIsExportOpen(!isExportOpen);
  }, [isExportOpen]);

  const openFilterDrawer = useCallback(() => {
    // Sync drawer filters with current applied filters when opening
    setDrawerFilters({ ...appliedFilters });
    setDrawerOpen((prev) => ({ ...prev, isOpen: true }));
  }, [appliedFilters]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen({ isOpen: false, data: null });
  }, []);

  // Memoized summary cards to prevent unnecessary re-renders
  const summaryCards = useMemo(
    () => [
      {
        title: t("Tickets sold"),
        value: eventCount?.ticketsSold,
        loading: eventCountLoading,
      },
      {
        title: t("Add-ons sold"),
        value: eventCount?.addonsSold,
        loading: eventCountLoading,
      },
      {
        title: t("Net sales"),
        value: eventCount?.netSales
          ? `${
              event?.data?.currency?.symbol || "$"
            } ${eventCount?.netSales?.toFixed(2)}`
          : 0,
        loading: eventCountLoading,
      },
    ],
    [eventCount, eventCountLoading]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-3">
        {!ticketType && (
          <div className="flex items-center gap-3">
            <Button
              buttonLabel={t("Filters")}
              buttonIcon={"fas fa-filter text-sm"}
              buttonFunction={openFilterDrawer}
            />
          </div>
        )}
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {summaryCards.map((card, index) => (
          <div
            key={index}
            className="bg-white border border-gray-200 rounded-lg p-6"
          >
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-gray-600 text-sm font-medium">
                {card.title}
              </h3>
              <i className="fas fa-info-circle text-gray-400 text-xs"></i>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-gray-900">
              {card.loading ? (
                <Skeleton width={index === 2 ? 120 : 60} height={36} />
              ) : (
                card.value
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Search and Controls */}
      <div className="flex flex-wrap gap-3 md:gap-0 items-center justify-between">
        <div className="flex flex-wrap items-center gap-1 md:gap-3">
          <div className="relative w-full md:w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={handleLimitChange}
            />
          </div>
          <div className="relative w-full md:w-72 mt-3">
            <Search placeholder={t("Search here")} search={handleSearch} />
          </div>
        </div>
        <div className="flex w-full md:w-auto gap-4 items-center">
          <ExportDropdown
            onExport={handleExport}
            isOpen={isExportOpen}
            onToggle={toggleExportDropdown}
            isLoading={isExporting}
          />
        </div>
      </div>

      {/* Ticket Sales Table */}
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={handlePageChange}
        originalData={[...(data?.data || [])]}
        setOriginalData={(val) => {
          queryClient.setQueryData(queryKey, (state) => {
            if (state) {
              return { ...state, data: val };
            }
          });
        }}
        isFetching={isFetching}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
      />

      {/* Ticket Sale Details Drawer */}
      <TicketSaleDrawer
        open={drawerOpen.isOpen}
        close={closeDrawer}
        filters={drawerFilters}
        setFilters={setDrawerFilters}
        onRunReport={handleRunReport}
      />
    </div>
  );
};

export default TicketSaleList;
